import React from 'react';
import { useSelector } from 'react-redux';

import FlagIcon from 'cccisd-icons/flag7';
import InfoIcon from 'cccisd-icons/info2';

import Tooltip from 'cccisd-tooltip';

import { toLocalDate } from '../../helpers';

import style from './style.css';

const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

const AlertsResponseRateBox = () => {
    const backgroundColor = '#d9544d';

    const youthList = useSelector(state =>
        isSupervisor ? state.app.supervisor.filteredYouthList : state.app.careCoordinator.youthList
    );
    const selectedYouth = useSelector(state => state.app.dashboard.selectedYouth);

    // filters
    const startDateFilterValue = useSelector(state => state.app.dashboard.filters.startDate);
    const endDateFilterValue = useSelector(state => state.app.dashboard.filters.endDate);
    const planNameFilterValue = useSelector(state => state.app.dashboard.filters.planName);

    const relevantYouth = selectedYouth ? [selectedYouth] : youthList;

    const criticalFlags = relevantYouth.flatMap(youth => (youth.flags || []).filter(flag => flag.critical));

    const criticalFlagsFiltered = criticalFlags
        .filter(flag => !endDateFilterValue || new Date(toLocalDate(flag.date)) <= new Date(endDateFilterValue))
        .filter(flag => !startDateFilterValue || new Date(toLocalDate(flag.date)) >= new Date(startDateFilterValue))
        .filter(flag => !planNameFilterValue || flag.planName.toLowerCase() === planNameFilterValue.toLowerCase());

    const getDaysFlagHasBeenFlagged = flag => {
        // expired flags count for a max of 14 days
        if (flag.unflaggedReason === 'expired') {
            return 14;
        }

        // unresolved flags return current time that has passed since they were flagged
        if (flag.status === 'flagged') {
            return (Date.now() - new Date(toLocalDate(flag.date))) / (1000 * 60 * 60 * 24);
        }

        // find the latest time that the flag was marked as resolved and use that date - the flagged date
        // latest transition from flagged to unflagged OR oldest log if all are unflagged
        const sortedLog = flag.log && [...flag.log].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        const latestUnflaggedEntry =
            sortedLog &&
            (sortedLog.find((entry, i) => entry.status === 'unflagged' && sortedLog[i + 1]?.status === 'flagged') ||
                (sortedLog.every(e => e.status === 'unflagged') ? sortedLog[0] : null));
        if (latestUnflaggedEntry) {
            return (
                (new Date(latestUnflaggedEntry.updatedAt) - new Date(toLocalDate(flag.date))) / (1000 * 60 * 60 * 24)
            );
        }

        // if all else fails, return now - flag date
        // all conditions above should work, this case should never apply
        console.warn('getDaysFlagHasBeenFlagged() could not find a value.');
        return (Date.now() - new Date(toLocalDate(flag.date))) / (1000 * 60 * 60 * 24);
    };

    const resolvedCount = criticalFlagsFiltered.filter(flag => flag.status === 'unflagged').length;
    const totalDays = criticalFlagsFiltered.reduce((sum, flag) => sum + getDaysFlagHasBeenFlagged(flag), 0);
    const avgDays =
        criticalFlagsFiltered.length === 0 ? '—' : Math.round(totalDays / criticalFlagsFiltered.length) + ' days';

    return (
        <div className={style.response_rate_box} style={{ backgroundColor }}>
            <span className={style.response_rate_alerts}>
                <FlagIcon /> Critical Alerts{' '}
            </span>
            <div className={style.dividing_line} />
            <div className={style.response_rate_stats} style={{ justifyContent: 'center' }}>
                <div className={style.response_rate_stat}>
                    <div className={style.response_rate_stat_label}>
                        Average Final Resolution Time{' '}
                        <Tooltip
                            title="Calculated by the last time an alert was marked as 'Resolved' minus the date the alert was triggered. Averaged across all alerts."
                            placement="top"
                        >
                            <InfoIcon />
                        </Tooltip>
                    </div>
                    <div className={style.response_rate_percentage}>{avgDays}</div>
                    <div className={style.response_rate_count}>
                        {resolvedCount}/{criticalFlags.length} resolved
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlertsResponseRateBox;
